/**
 * The read side of the content API: legal documents, FAQ and news.
 *
 * Wire shapes here are TRANSCRIBED from the controllers that produce them —
 * `backend/src/core/admin/cms/cms-public.controller.ts` and
 * `backend/src/game/consent/consent.controller.ts` — not invented. If one of
 * them changes, this file is the single place the client has to follow.
 *
 * ── WHY A LEGAL PAGE IS FETCHED FROM TWO DIFFERENT ENDPOINTS ────────────────
 * `GET /cms/legal/:slug` serves the LIVE, editable CMS row. An admin can
 * change it at any moment and it carries no version and no hash.
 * `GET /consent/documents/:purpose` (or `/consent/documents/slug/:slug` for
 * a document with no consent purpose) serves the FROZEN snapshot that consent
 * records point at — the text a player accepted, which nobody can edit
 * afterwards.
 *
 * For terms and privacy the published snapshot is the one that matters: it is
 * the text in force, and it is the text whose version + hash a registration
 * screen must submit. Showing the live CMS row while recording consent against
 * the snapshot would put "agreed to text they were never shown" into the audit
 * trail. So this module PREFERS the snapshot and falls back to the CMS row,
 * and it says which one it returned in `source` so the page can be honest
 * about an unpublished draft rather than presenting it as binding.
 */
import { api, ApiError } from '@/core/network/api';

/* ═════════════════════════════════════════════════════════════ wire shapes ══ */

/** A row of core's `legal_pages`. Mutable, unversioned, no hash. */
export interface CmsLegalPage {
  readonly id: string;
  readonly slug: string;
  readonly title: string;
  readonly content: string;
  readonly active: boolean;
  readonly updatedAt: string;
}

/** Purposes a consent may be recorded for. Mirrors the Prisma enum. */
export type ConsentPurposeName =
  | 'TERMS_OF_SERVICE'
  | 'PRIVACY_POLICY'
  | 'MARKETING_EMAIL'
  | 'ANALYTICS';

/** `LegalDocumentRef` from the consent service — metadata, no body. */
export interface LegalDocumentRef {
  readonly id: string;
  readonly slug: string;
  readonly version: number;
  readonly purpose: ConsentPurposeName | null;
  readonly title: string;
  readonly contentHash: string;
  readonly locale: string | null;
  readonly effectiveFrom: string;
  readonly supersededAt: string | null;
  readonly publishedAt: string;
}

/** `LegalDocumentSnapshot` — a ref with the frozen body attached. */
export interface LegalDocumentSnapshot extends LegalDocumentRef {
  readonly content: string;
}

export interface FaqEntry {
  readonly id: string;
  readonly question: string;
  readonly answer: string;
  readonly sortOrder: number;
  readonly active: boolean;
}

export interface NewsArticle {
  readonly id: string;
  readonly slug: string;
  readonly title: string;
  readonly summary: string;
  readonly content: string;
  readonly imageUrl: string | null;
  readonly tag: string;
  readonly published: boolean;
  readonly publishedAt: string | null;
}

export interface NewsPage {
  readonly items: readonly NewsArticle[];
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
}

/* ═════════════════════════════════════════════════════════ legal documents ══ */

/**
 * The documents this game publishes, in the order a legal index lists them.
 *
 * `purpose` mirrors the front matter in `content/legal/*.md` and the binding
 * the seed publishes with. It is the map from a URL slug to the consent
 * endpoint that serves the frozen text.
 */
export const LEGAL_DOCUMENTS: readonly {
  readonly slug: string;
  readonly purpose: ConsentPurposeName | null;
}[] = [
  { slug: 'terms', purpose: 'TERMS_OF_SERVICE' },
  { slug: 'privacy', purpose: 'PRIVACY_POLICY' },
  { slug: 'cookies', purpose: 'ANALYTICS' },
  { slug: 'refunds', purpose: null },
];

export function purposeForSlug(slug: string): ConsentPurposeName | null {
  return LEGAL_DOCUMENTS.find((d) => d.slug === slug)?.purpose ?? null;
}

/** The public endpoint that serves the frozen version in force for a document. */
function snapshotPath(slug: string, purpose: ConsentPurposeName | null): string {
  return purpose !== null
    ? `/consent/documents/${purpose}`
    : `/consent/documents/slug/${encodeURIComponent(slug)}`;
}

/** Where the text on screen came from, and therefore what may be claimed about it. */
export type LegalDocumentSource = 'published' | 'draft';

/**
 * One legal document, ready to render.
 *
 * `source: 'published'` — a frozen version. `version` and `contentHash` are
 * present and are exactly what a consent POST must echo back.
 * `source: 'draft'` — the live CMS row, because nothing has been published for
 * this slug yet. It has no version and no hash, it is NOT in force, and the
 * page must say so.
 */
export interface LegalDocumentView {
  readonly slug: string;
  readonly title: string;
  readonly content: string;
  readonly source: LegalDocumentSource;
  readonly purpose: ConsentPurposeName | null;
  readonly version: number | null;
  readonly contentHash: string | null;
  readonly effectiveFrom: string | null;
  readonly updatedAt: string | null;
  readonly locale: string | null;
}

export interface FetchOptions {
  readonly signal?: AbortSignal;
}

const isNotFound = (err: unknown): boolean => err instanceof ApiError && err.status === 404;

/** Rethrow anything that is not a 404, so a real outage is never read as "no such page". */
function only404<T>(err: unknown, fallback: T): T {
  if (isNotFound(err)) return fallback;
  throw err;
}

/**
 * Fetch a legal document by slug: the published snapshot if there is one, the
 * CMS draft if there is not.
 *
 * Throws the underlying `ApiError` when neither exists — a 404 the caller
 * renders as "no such document", anything else as a failure.
 */
export async function fetchLegalDocument(
  slug: string,
  opts: FetchOptions = {},
): Promise<LegalDocumentView> {
  const purpose = purposeForSlug(slug);

  // A document WITH a consent purpose is read by purpose — the same call the
  // consent screens make, so the page and the consent record cannot disagree.
  // A document published for the record only (refunds: purpose null by
  // design) has no purpose to read by, so it is read by slug. Before the
  // by-slug route existed this skipped straight to the CMS row, and the
  // refund policy's published versions were never shown (owner asks §E).
  const snapshot = await api
    .get<LegalDocumentSnapshot>(snapshotPath(slug, purpose), { signal: opts.signal })
    .catch((err: unknown) => only404<LegalDocumentSnapshot | null>(err, null));

  // A published document for this PURPOSE that belongs to a different SLUG
  // is not the page that was asked for. The consent service binds a purpose
  // to one slug for life, so this only happens if the map above has drifted
  // from what was actually published — fall through and show the CMS page
  // rather than serving the wrong document under this URL.
  if (snapshot !== null && snapshot.slug === slug) {
    return {
      slug: snapshot.slug,
      title: snapshot.title,
      content: snapshot.content,
      source: 'published',
      purpose: snapshot.purpose,
      version: snapshot.version,
      contentHash: snapshot.contentHash,
      effectiveFrom: snapshot.effectiveFrom,
      updatedAt: null,
      locale: snapshot.locale,
    };
  }

  const page = await api.get<CmsLegalPage>(`/cms/legal/${slug}`, { signal: opts.signal });
  return {
    slug: page.slug,
    title: page.title,
    content: page.content,
    source: 'draft',
    purpose,
    version: null,
    contentHash: null,
    effectiveFrom: null,
    updatedAt: page.updatedAt,
    locale: null,
  };
}

/** An entry in the legal index. `version === null` means nothing is published yet. */
export interface LegalIndexEntry {
  readonly slug: string;
  readonly title: string;
  readonly version: number | null;
  readonly effectiveFrom: string | null;
  readonly source: LegalDocumentSource;
}

/**
 * List the legal documents, published ones first-class and the rest honestly
 * labelled.
 *
 * One request lists everything with a consent purpose. Documents WITHOUT a
 * purpose — the refund policy — are looked up individually: their published
 * version by slug (`/consent/documents/slug/:slug`), else the CMS draft. A missing one is simply absent
 * from the index rather than an error: an operator who has not created it yet
 * should see a short list, not a broken page.
 */
export async function fetchLegalIndex(opts: FetchOptions = {}): Promise<LegalIndexEntry[]> {
  const published = await api
    .get<{ documents: LegalDocumentRef[] }>('/consent/documents', { signal: opts.signal })
    .then((r) => r.documents)
    .catch((err: unknown) => only404<LegalDocumentRef[]>(err, []));

  const bySlug = new Map(published.map((d) => [d.slug, d]));
  const entries: LegalIndexEntry[] = [];

  for (const doc of LEGAL_DOCUMENTS) {
    const ref = bySlug.get(doc.slug);
    if (ref !== undefined) {
      entries.push({
        slug: ref.slug,
        title: ref.title,
        version: ref.version,
        effectiveFrom: ref.effectiveFrom,
        source: 'published',
      });
      continue;
    }
    // Not in the by-purpose listing: a purpose-less document (refunds). Its
    // published version, if it has one, is still the thing to list.
    if (doc.purpose === null) {
      const snapshot = await api
        .get<LegalDocumentSnapshot>(snapshotPath(doc.slug, null), { signal: opts.signal })
        .catch((err: unknown) => only404<LegalDocumentSnapshot | null>(err, null));
      if (snapshot !== null) {
        entries.push({
          slug: snapshot.slug,
          title: snapshot.title,
          version: snapshot.version,
          effectiveFrom: snapshot.effectiveFrom,
          source: 'published',
        });
        continue;
      }
    }
    const page = await api
      .get<CmsLegalPage>(`/cms/legal/${doc.slug}`, { signal: opts.signal })
      .catch((err: unknown) => only404<CmsLegalPage | null>(err, null));
    if (page !== null) {
      entries.push({
        slug: page.slug,
        title: page.title,
        version: null,
        effectiveFrom: null,
        source: 'draft',
      });
    }
  }

  return entries;
}

/* ════════════════════════════════════════════════════════════════ FAQ ══ */

export async function fetchFaq(opts: FetchOptions = {}): Promise<FaqEntry[]> {
  return api.get<FaqEntry[]>('/cms/faq', { signal: opts.signal });
}

/* ═══════════════════════════════════════════════════════════════ news ══ */

export async function fetchNews(page = 1, opts: FetchOptions = {}): Promise<NewsPage> {
  const query = page > 1 ? `?page=${encodeURIComponent(String(page))}` : '';
  return api.get<NewsPage>(`/cms/news${query}`, { signal: opts.signal });
}

export async function fetchNewsArticle(
  slug: string,
  opts: FetchOptions = {},
): Promise<NewsArticle> {
  return api.get<NewsArticle>(`/cms/news/${slug}`, { signal: opts.signal });
}

/** True when a rejected request was a 404 rather than a failure. */
export function isNotFoundError(err: unknown): boolean {
  return isNotFound(err);
}

/**
 * True when a request was cancelled because the page unmounted.
 *
 * Axios reports an abort as a request with no response, which `ApiError`
 * flattens to `status: 0` — the same shape as "the network is down". The
 * difference matters: one should render an error, the other should render
 * nothing at all because the screen is already gone.
 */
export function isAbortError(err: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted === true) return true;
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  return err instanceof Error && (err.name === 'CanceledError' || err.name === 'AbortError');
}
